import { useContext, useRef } from 'react'
import { NavContext } from '@ionic/react'

export interface TabButtonConfig {
	tab: string
	href?: string
}

interface TabHistory {
	originalHref: string | undefined
	currentHref: string | undefined
	originalRouteOptions: unknown
	currentRouteOptions: unknown
}

interface RouteInfo {
	pathname: string
	search?: string
	routeAction?: string
	routeOptions?: unknown
}

interface TabEvents {
	onWillChange?: (tab: string) => void
	onDidChange?: (tab: string) => void
}

const activeTabOf = (tabs: Record<string, TabHistory>, pathname: string): string | undefined =>
	Object.keys(tabs).find(key => tabs[key].originalHref !== undefined && pathname.startsWith(tabs[key].originalHref))

/**
 * Ionic's tab bar bookkeeping, kept to its letter: the tab whose href the path starts with is active, each tab
 * remembers where it was left, and tapping the active tab takes it back to its own root.
 */
const useTabBar = (buttons: TabButtonConfig[], { onWillChange, onDidChange }: TabEvents = {}) => {
	const nav = useContext(NavContext)
	const routeInfo: RouteInfo = (nav.routeInfo as RouteInfo) || { pathname: window.location.pathname }
	const state = useRef<{ tabs: Record<string, TabHistory>; activeTab?: string }>()

	if (!state.current) {
		const tabs: Record<string, TabHistory> = {}
		for (const { tab, href } of buttons) {
			const options = href === routeInfo.pathname ? routeInfo.routeOptions : undefined
			tabs[tab] = { originalHref: href, currentHref: href, originalRouteOptions: options, currentRouteOptions: options }
		}
		state.current = { tabs }
	}

	const tabs = { ...state.current.tabs }
	const activeTab = activeTabOf(state.current.tabs, routeInfo.pathname)
	for (const { tab, href } of buttons) {
		if (!tabs[tab] || tabs[tab].originalHref !== href) {
			tabs[tab] = {
				originalHref: href,
				currentHref: href,
				originalRouteOptions: undefined,
				currentRouteOptions: undefined,
			}
		}
	}
	const prevActiveTab = state.current.activeTab
	if (activeTab && prevActiveTab) {
		const prev = state.current.tabs[prevActiveTab]
		if (
			activeTab !== prevActiveTab ||
			prev.currentHref !== routeInfo.pathname ||
			prev.currentRouteOptions !== routeInfo.routeOptions
		) {
			tabs[activeTab] = {
				...tabs[activeTab],
				currentHref: routeInfo.pathname + (routeInfo.search || ''),
				currentRouteOptions: routeInfo.routeOptions,
			}
			if (routeInfo.routeAction === 'pop' && activeTab !== prevActiveTab) {
				tabs[prevActiveTab] = { ...tabs[prevActiveTab], currentHref: tabs[prevActiveTab].originalHref }
			}
		}
	}
	if (activeTab) nav.setCurrentTab(activeTab, routeInfo as never)
	state.current = { tabs, activeTab }

	const hrefFor = (tab: string): string | undefined => (tab === activeTab ? routeInfo.pathname : tabs[tab]?.currentHref)

	const select = (tab: string) => {
		const tapped = state.current.tabs[tab]
		const href = hrefFor(tab)
		if (state.current.activeTab === tab) {
			if (tapped.originalHref !== href) nav.resetTab(tab, tapped.originalHref, tapped.originalRouteOptions as never)
		} else {
			onWillChange?.(tab)
			onDidChange?.(tab)
			nav.changeTab(tab, href, undefined)
		}
	}

	return { activeTab, hrefFor, select }
}

export default useTabBar
